import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Pagination,
  Button,
  Grid,
} from "@mui/material";
import Breadcrumb from "../../../DefaultComponent/BreadCrumb";
import Spinner from "../../../DefaultComponent/Spinner";
import ToastDefault from "../../../DefaultComponent/Toast";
import { formatDate, calculateTotalPages } from "../../../../utils/Helper";
import {
  JobDetailAction,
  JobApplicantsListAction,
} from "../../../../Redux/Actions/CareerAction";
import { useNavigate, useParams } from "react-router-dom";

interface Skill {
  id: number;
  name: string;
}

interface JobDetailData {
  id: number;
  attachments: any[];
  title: string;
  meta_data: string;
  company_description: string;
  role_description: string;
  requirements: string;
  responsibilities: string;
  qualifications: string;
  benefits: string;
  location: string;
  salary: string;
  start_date: string;
  end_date: string | null;
  vacancies: string;
  industry: string;
  job_type: string;
  workplace_type: string;
  created_at: string;
  updated_at: string;
  required_skills: Skill[];
}

interface Applicant {
  id: number;
  applicant_name: string;
  applicant_email: string;
  contact_no: string;
  date_applied: string;
  status: string;
  resume: string;
  linkedin_url?: string;
  description?: string;
}

const Section = ({ title, content }: { title: string; content: string }) => (
  <Box sx={{ mb: 3 }}>
    <Typography variant="h6" fontWeight={600} gutterBottom>
      {title}
    </Typography>
    <Typography sx={{ textAlign: "justify" }}>{content}</Typography>
  </Box>
);

const MetaItem = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Typography variant="subtitle2" fontWeight={600}>
      {label}
    </Typography>
    <Typography>{value}</Typography>
  </Box>
);

const JobDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const detailState = useSelector((state: any) => state.JobDetailData);
  const applicantsState = useSelector(
    (state: any) => state.JobApplicantsListData
  );

  const [page, setPage] = useState<number>(1);
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(JobDetailAction(`${jobId}/`));
  }, [dispatch, jobId]);

  useEffect(() => {
    dispatch(
      JobApplicantsListAction(`${jobId}/?page=${page}&page_size=${rowsPerPage}`)
    );
  }, [dispatch, jobId, page]);

  const job: JobDetailData | undefined = detailState?.data;
  const applicants: Applicant[] = applicantsState?.data?.results || [];
  const totalApplicants = applicantsState?.data?.total_count || 0;
  const loading = detailState.loading || applicantsState.loading;

  if (loading || !job) return <Spinner />;

  return (
    <>
      <div
        className="page-wrapper card"
        style={{ display: "flex", flexDirection: "column" }}
      >
        <div className="col-md-12">
          <Breadcrumb
            pageTitle="Job Details"
            breadcrumbs={[
              { label: "Jobs", path: "/careers/jobs", isActive: false },
              { label: job.title, path: `/jobs/${job.id}`, isActive: true },
            ]}
          />
        </div>

        <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
          ← Back to list
        </Button>

        <Box sx={{ p: 2 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {job.title}
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            {job.meta_data}
          </Typography>

          <Box sx={{ maxWidth: "1200px", mx: "auto", p: 2 }}>
            <Grid container spacing={4}>
              {/* LEFT COLUMN */}
              <Grid item xs={12} md={7}>
                <Box sx={{ pr: { md: 4 } }}>
                  <Section title="Company" content={job.company_description} />
                  <Section title="Role" content={job.role_description} />
                  <Section
                    title="Requirements"
                    content={job.requirements ? job.requirements : "N/A"}
                  />
                  <Section
                    title="Responsibilities"
                    content={job.responsibilities}
                  />
                  <Section
                    title="Qualifications"
                    content={job.qualifications}
                  />
                </Box>
              </Grid>

              {/* RIGHT COLUMN */}
              <Grid item xs={12} md={5}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <MetaItem label="Benefits" value={job.benefits || "N/A"} />
                  <MetaItem label="Location" value={job.location} />
                  <MetaItem label="Salary" value={job.salary} />
                  <MetaItem
                    label="Start Date"
                    value={formatDate(job.start_date)}
                  />
                  <MetaItem
                    label="End Date"
                    value={job.end_date ? formatDate(job.end_date) : "N/A"}
                  />
                  <MetaItem label="Vacancies" value={job.vacancies} />
                  <MetaItem label="Industry" value={job.industry} />
                  <MetaItem label="Job Type" value={job.job_type} />
                  <MetaItem label="Workplace Type" value={job.workplace_type} />
                  <MetaItem
                    label="Skills"
                    value={job.required_skills.map((s) => s.name).join(", ")}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Box sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            Applicants ({totalApplicants})
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <TableContainer component={Paper} sx={{ minWidth: 800 }}>
              <Table aria-label="Applicants Table">
                <TableHead sx={{ backgroundColor: "#0555c8" }}>
                  <TableRow>
                    <TableCell sx={{ color: "white" }}>S.No</TableCell>
                    <TableCell sx={{ color: "white" }}>Name</TableCell>
                    <TableCell sx={{ color: "white" }}>Email</TableCell>
                    <TableCell sx={{ color: "white" }}>Contact No</TableCell>
                    <TableCell sx={{ color: "white" }}>Date Applied</TableCell>
                    <TableCell sx={{ color: "white" }}>Status</TableCell>
                    <TableCell sx={{ color: "white" }}>Resume</TableCell>
                    <TableCell sx={{ color: "white" }}>LinkedIn</TableCell>
                    <TableCell sx={{ color: "white" }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applicants.map((app, idx) => (
                    <TableRow key={app.id}>
                      <TableCell>
                        {(page - 1) * rowsPerPage + idx + 1}
                      </TableCell>
                      <TableCell>{app.applicant_name}</TableCell>
                      <TableCell>{app.applicant_email}</TableCell>
                      <TableCell>{app.contact_no}</TableCell>
                      <TableCell>{formatDate(app.date_applied)}</TableCell>
                      <TableCell>{app.status}</TableCell>
                      <TableCell>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(app.resume, "_blank")}
                        >
                          View Resume
                        </Button>
                      </TableCell>
                      <TableCell>
                        {app.linkedin_url ? (
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() =>
                              window.open(app.linkedin_url, "_blank")
                            }
                          >
                            LinkedIn
                          </Button>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell
                        sx={{
                          maxWidth: 400,
                        }}
                      >
                        {app.description || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
            <Pagination
              count={calculateTotalPages(totalApplicants, rowsPerPage)}
              page={page}
              onChange={(e, newPage) => setPage(newPage)}
              color="primary"
              variant="outlined"
              shape="rounded"
            />
          </Box>
        </Box>
      </div>
      <ToastDefault />
    </>
  );
};

export default JobDetail;
